import {
  OdProtosSellCommon_Product as ProtoProduct,
  OdProtosSellSellerData_SellerExperience as SellerExperience,
} from '__generated__/globalTypes';
import type { AvailableProduct, SellerProducts } from 'sellDirect/declarations/products';
import { isAvailableProduct } from 'sellDirect/declarations/products';
import type { SellerTransactionQuery_sellerProducts_SellerProductsQuery_sellerTransaction as SellerTransaction } from 'sellDirect/graphql/__generated__/useSellerTransaction';
import type {
  PartnershipReferralsFragment_SellerTransactionPartnershipReferrals_referralFlags_SellerTransactionReferralFlags as PartnershipFlags,
  PartnershipReferralsFragment,
} from 'sellDirect/graphql/fragments/__generated__/PartnershipReferralsFragment';

type TransactionWithSellerExperience = Extract<
  SellerTransaction,
  { sellerExperience: SellerExperience }
>;

type TransactionWithPartnershipReferrals = Extract<
  SellerTransaction,
  { partnershipReferrals: PartnershipReferralsFragment | null | undefined }
>;

/** Type guard that returns true when the transaction exists and its sellerExperience is AGENT_ACQUISITION */
export const isAgentExperience = (
  sellerTransaction: SellerTransaction | null | undefined,
): sellerTransaction is TransactionWithSellerExperience =>
  sellerTransaction != null &&
  'sellerExperience' in sellerTransaction &&
  sellerTransaction.sellerExperience === SellerExperience.SELLER_EXPERIENCE_AGENT_ACQUISITION;

/** Type guard that returns true when the transaction exists and its partnershipReferrals is present */
const hasPartnershipReferrals = (
  sellerTransaction: SellerTransaction | null | undefined,
): sellerTransaction is TransactionWithPartnershipReferrals =>
  sellerTransaction != null && 'partnershipReferrals' in sellerTransaction;

/** Extracts blockBuyerContent from sellerTransaction.partnershipReferrals */
export const extractBlockBuyerContent = (
  sellerTransaction: SellerTransaction | null | undefined,
): boolean => {
  if (!hasPartnershipReferrals(sellerTransaction)) return false;
  return sellerTransaction.partnershipReferrals?.blockBuyerContent ?? false;
};

/** Extracts isPartnershipLead from sellerTransaction.partnershipReferrals */
export const extractIsPartnershipLead = (
  sellerTransaction: SellerTransaction | null | undefined,
): boolean => {
  if (!hasPartnershipReferrals(sellerTransaction)) return false;
  return sellerTransaction.partnershipReferrals?.isPartnershipLead ?? false;
};

/** Extracts referralFlags from sellerTransaction.partnershipReferrals */
export const extractPartnershipFlags = (
  sellerTransaction: SellerTransaction | null | undefined,
): PartnershipFlags | null => {
  if (!hasPartnershipReferrals(sellerTransaction)) return null;
  return sellerTransaction.partnershipReferrals?.referralFlags ?? null;
};

const EMPTY_SELLER_PRODUCTS: SellerProducts = {
  availableProducts: [],
  selectedProduct: undefined,
  listingProduct: undefined,
  cashOfferProduct: undefined,
  cashPlusProduct: undefined,
  previouslyEligibleProducts: [],
};

/**
 * Extracts seller-product slots from `sellerTransaction.acquisitionProducts`,
 * `selectedAcquisitionProduct.kind`, and `previouslyEligibleProducts`.
 *
 * `availableProducts` lists displayable products only (Cash + Cash Plus); the
 * Listing slot has its own dedicated `listingProduct` field. `selectedProduct`
 * is filtered through `isAvailableProduct` so that LIST_WITH_CERTAINTY collapses
 * to undefined the way the legacy productsExperiences derivation did.
 */
export const extractSellerProducts = (
  sellerTransaction: SellerTransaction | null | undefined,
): SellerProducts => {
  if (!sellerTransaction || !('acquisitionProducts' in sellerTransaction)) {
    return EMPTY_SELLER_PRODUCTS;
  }

  const { sellDirect, sellWithUpside, listing } = sellerTransaction.acquisitionProducts;

  const availableProducts: AvailableProduct[] = [];
  if (sellDirect) {
    availableProducts.push(ProtoProduct.OPENDOOR_DIRECT);
  }
  if (sellWithUpside) {
    availableProducts.push(ProtoProduct.SELL_WITH_UPSIDE);
  }

  const selectedKind = sellerTransaction.selectedAcquisitionProduct?.kind;
  const selectedProduct =
    selectedKind && isAvailableProduct(selectedKind) ? selectedKind : undefined;

  // gql nulls -> interface uses optional `?:` (T | undefined); coerce at assignment.
  return {
    availableProducts,
    selectedProduct,
    listingProduct: listing ?? undefined,
    cashOfferProduct: sellDirect ?? undefined,
    cashPlusProduct: sellWithUpside ?? undefined,
    previouslyEligibleProducts: sellerTransaction.previouslyEligibleProducts,
  };
};
